import classNames from 'classnames'

import { useMyMonitoringDeleteUnitTrigger } from '@apps-common/apis/my-monitoring/triggers'
import { useDeleteUnitTrigger } from '@apps-common/apis/portal/trigger'
import { TriggerOptionsModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/triggers/modals'
import { ButtonRoundLg } from '@apps-common/components/elements/button'
import { ModalButton } from '@apps-common/components/modals'
import { useHistoryModal } from '@apps-common/components/modals/unit-history/HistoryModal'
import { CID_EVENT_CODES, CID_EVENT_TYPE_EVENT, CID_EVENT_TYPE_RESTORE } from '@apps-common/config/cid-event-codes'
import { commonIcons } from '@apps-common/config/icons'
import { TRIGGER_TYPE_ARM_DISARM, TRIGGER_TYPE_EVENT } from '@apps-common/config/triggers'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { ENV_APP_PROJECT } from '@apps-common/lib/environment'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitTriggerModel } from '@apps-common/types/portal/unit-trigger'
import { invalidateTriggerQueries } from '../../../../../../../../../lib/invalidateTriggerQueries'
import { useTriggerModifyModal } from '../hooks/useTriggerModifyModal'

const project = ENV_APP_PROJECT

interface IProps {
  unitStatus: string
  unitId: string
  productId: string
  trigger: IUnitTriggerModel
  disabled?: boolean
  // Modal?: any
  // deleteHook?: any
}

export const TriggerListCard: React.FC<IProps> = ({ unitStatus, unitId, productId, trigger, disabled }) => {
  const {
    unitTriggerId,
    unitTriggerType,
    active,
    triggerName,
    controlName,
    desiredState,
    eventType,
    eventCode,
    partitionCheck,
    partition,
    zoneCheck,
    zone
  } = trigger

  const btnColor = useProjectHook('bg-brand text-on-brand', 'bg-ctx-mm-brand text-ctx-on-mm-brand')
  const useDeleteHook = useProjectHook(useDeleteUnitTrigger, useMyMonitoringDeleteUnitTrigger)

  const { mutate: deleteMutation } = useDeleteHook(true, () => {
    invalidateTriggerQueries(unitTriggerId, unitId)
  })

  const { present } = useTriggerModifyModal({
    status: unitStatus,
    unitId,
    productId,
    triggerId: unitTriggerId
  })

  const { open } = useHistoryModal({ type: 'trigger', name: triggerName, id: unitTriggerId })

  const onOptionSelected = ({ action }: { action: string }) => {
    if (action === 'edit') {
      present()
    } else if (action === 'delete') {
      deleteMutation({ triggerId: unitTriggerId })
    } else if (action === 'history') {
      open()
    }
  }

  const NavigateIcon = commonIcons.arrowAngled
  const event = CID_EVENT_CODES.find((e) => e.code === eventCode)

  return (
    <ModalButton
      component={TriggerOptionsModal}
      disabled={disabled}
      componentProps={{ unitStatus, trigger }}
      classes='relative w-full !opacity-100'
      confirmCallback={onOptionSelected}
      backdropDismiss
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
    >
      <div className='flex flex-col items-between justify-center gap-[15px] text-left bg-surface border border-border px-4 py-4 rounded-[35px] overflow-hidden relative'>
        <div className='absolute top-0 right-0 flex items-center justify-end gap-[5px] pr-5 px-4 py-2 bg-surface-2 rounded-bl-[15px]'>
          <p className='jost-light text-[0.8em]'>{active ? 'Active' : 'Disabled'}</p>
          <div className='relative'>
            <div
              className={classNames(
                'absolute top-[1px] left-[1px] w-[13px] h-[13px] rounded-[10px]',
                active && 'bg-success animate-ping',
                !active && 'bg-error'
              )}
            />
            <div className={classNames('w-[15px] h-[15px] rounded-[10px]', active && 'bg-success', !active && 'bg-error')} />
          </div>
        </div>
        <p className='jost-light capitalize text-text leading-[1em] pl-[15px] px-[7.5px] py-[3px] max-w-[80%] overflow-hidden text-nowrap text-ellipsis'>
          {triggerName}
        </p>
        <div className='flex items-end justify-between text-text border border-border pl-5 pr-4 py-4 rounded-[35px]'>
          <div className='grid sm:grid-cols-2 gap-[10px] grow'>
            <div>
              <label className='jost-medium text-[0.8em]'>Control</label>
              <p className='jost-light'>{controlName}</p>
            </div>
            <div>
              <label className='jost-medium text-[0.8em]'>Action</label>
              <p className='jost-light'>
                Turn {controlName} {desiredState}
              </p>
            </div>
            <div className='sm:col-span-2'>
              <label className='jost-medium text-[0.8em]'>Trigger on</label>
              <div className='flex'>
                <p className='jost-light flex flex-col sm:flex-row sm:items-center sm:justify-start gap-[10px]'>
                  {unitTriggerType === TRIGGER_TYPE_ARM_DISARM.value && (
                    <div className='flex items-center justify-start gap-[5px] bg-surface-2 py-[4px] px-[6px] rounded-lg'>
                      <span>Alarm Arm/Disarm</span>
                      <span
                        className={classNames(
                          'jost h-[20px] rounded-md flex items-center justify-center px-[4px]',
                          eventType === CID_EVENT_TYPE_RESTORE.value && 'bg-success/60 text-on-success ring-1 ring-success',
                          eventType === CID_EVENT_TYPE_EVENT.value && 'bg-error/60 text-on-error ring-1 ring-error'
                        )}
                      >
                        {eventType === CID_EVENT_TYPE_RESTORE.value && 'Arm'}
                        {eventType === CID_EVENT_TYPE_EVENT.value && 'Disarm'}
                      </span>
                    </div>
                  )}
                  {unitTriggerType === TRIGGER_TYPE_EVENT.value && (
                    <div className='flex items-center justify-start gap-[5px] bg-surface-2 py-[4px] px-[6px] rounded-lg'>
                      <span>{event?.description || 'None specified'}</span>
                      <span
                        className={classNames(
                          'jost w-[20px] h-[20px] rounded-md flex items-center justify-center',
                          eventType === CID_EVENT_TYPE_RESTORE.value && 'bg-success/60 text-on-success ring-1 ring-success',
                          eventType === CID_EVENT_TYPE_EVENT.value && 'bg-error/60 text-on-error ring-1 ring-error'
                        )}
                      >
                        {eventType}
                      </span>
                    </div>
                  )}
                  <div className='flex items-center justify-start gap-[10px]'>
                    {zoneCheck && (
                      <div className='jost bg-surface-2 py-[4px] px-[6px] rounded-md flex items-center justify-center gap-[5px] text-[0.9em]'>
                        <div className='jost-light'>Zone</div>
                        <div className='bg-surface-2 px-1 rounded-md'>{zone}</div>
                      </div>
                    )}
                    {partitionCheck && (
                      <div className='jost bg-surface-2 py-[4px] px-[6px] rounded-md flex items-center justify-center gap-[5px] text-[0.9em]'>
                        <div className='jost-light'>Partition</div>
                        <div className='bg-surface-2 px-1 rounded-md'>{partition}</div>
                      </div>
                    )}
                  </div>
                </p>
              </div>
            </div>
          </div>
          {!disabled && (
            <ButtonRoundLg type='button' className={btnColor}>
              <NavigateIcon />
            </ButtonRoundLg>
          )}
        </div>
      </div>
    </ModalButton>
  )
}
